import React, { useState, memo, useMemo, useEffect, useCallback } from "react";
import { Row, Col } from 'react-bootstrap';
import { motion } from "framer-motion";
import "./index.scss";

import SkeletonLoader from "../../../../../components/ui/SkeletonLoader";

import { formatDate } from "../../../../../utils/util";

import GaugeChart from 'react-gauge-chart';

import { trendingDownIcon, trendingUpIcon } from "../../../../../utils/icons";

import { useScanInfoContext } from "../../../../ScanInfo/ScanInfoContext";

import { useSelector, useDispatch } from 'react-redux';
import { getDomainRiskScore, getDomainRiskScoreOverview } from "../../../../../store/actions/dashboardAction";

const Gauge = memo(({ percent }) => {
    const chartStyle = useMemo(() => ({ height: 54, width: 120 }), []);

    const gaugeProps = useMemo(
        () => ({
            id: "gauge-chart1",
            nrOfLevels: 4,
            colors: ["#ee6862", "#f2a16b", "#F4DA6B", "#8dd761"],
            percent,
            arcWidth: 0.2,
            arcPadding: 0,
            cornerRadius: 0,
            hideText: true,
            style: chartStyle,
        }),
        [percent, chartStyle]
    );

    return <GaugeChart {...gaugeProps} />;
});


const DomainRiskScore = () =>{

    const domainRiskScoreResponse = useSelector((state) => state?.dashboard?.domainRiskScore)
    const domainRiskScoreOverviewResponse = useSelector((state)=> state?.dashboard?.domainRiskScoreOverview)

    const dispatch = useDispatch();

    const {
        domainId,
        scanId,
        triggerStatsFetch,
        setTriggerStatsFetch,
    } = useScanInfoContext();

    const [domainRiskScoreLoading, setDomainRiskScoreLoading] = useState(false);
    const [domainRiskScoreOverviewLoading, setDomainRiskScoreOverviewLoading] = useState(false);
    const [domainRiskScoreOverviewData, setDomainRiskScoreOverviewData] = useState(null);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [isOverviewScanNotFound, setIsOverviewScanNotFound] = useState(false)

    const fetchDomainRiskScore = useCallback(()=>{
        setDomainRiskScoreLoading(true)
        const params = `&domain=${domainId}&scan_id=${scanId}`;
        dispatch(getDomainRiskScore(params))
    },[domainId, scanId, dispatch])

    const fetchDomainRiskScoreOverview = useCallback(()=>{
        setDomainRiskScoreOverviewLoading(true)
        const params = `&domain=${domainId}`;
        dispatch(getDomainRiskScoreOverview(params))
    },[domainId, dispatch])

    useEffect(() => {
        if (triggerStatsFetch) {
            fetchDomainRiskScore();
            fetchDomainRiskScoreOverview();
            setTriggerStatsFetch(false);
        }
    }, [triggerStatsFetch, setTriggerStatsFetch, fetchDomainRiskScore, fetchDomainRiskScoreOverview]);

    useEffect(()=>{
        if(domainRiskScoreResponse !== null) {
            setDomainRiskScoreLoading(false)
        }
    },[domainRiskScoreResponse])

    useEffect(()=>{
        if(domainRiskScoreOverviewResponse !== null) {
            setDomainRiskScoreOverviewLoading(false)
            const domainScore = domainRiskScoreOverviewResponse?.stats?.domain_score;
            const weeks = {};
            if(domainRiskScoreOverviewResponse?.status === "failed"){
                setDomainRiskScoreOverviewData(null);
                setIsOverviewScanNotFound(true);
                return
            } else {
                setIsOverviewScanNotFound(false);
                domainScore.forEach((month) => {
                    month.weeks.forEach((week, index) => {
                        const weekName = `week${index + 1}`;
    
                        // Initialize array for week if not created
                        if (!weeks[weekName]) {
                            weeks[weekName] = [];
                        }
    
                        // Push month info into correct week group
                        weeks[weekName].push({
                            month: month.name,
                            score: week.score,
                            scan_date: week.scan_date,
                            risk_level: week.risk_level,
                            key: `${month.name}_${weekName}`
                        });
                    });
                });
    
                setDomainRiskScoreOverviewData({ stats: { domain_score: weeks } });
            }
        }
    },[domainRiskScoreOverviewResponse])

    return (
        <div className="domain-risk-score-container mt-4">
            <Row>
                <Col lg="4">
                    <div className="bg-white-with-shadow p-4 h-100">
                        {
                            domainRiskScoreLoading ?
                            <SkeletonLoader type={"line"} count={5} currentPage={"domainRiskScore"} />
                            :
                            <motion.div
                                key={"domainRiskScore"}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <p className="heading">Domain risk score</p>
                                    <div className="d-flex align-items-center gap-5">
                                        <div className="gauage-container mt-3">
                                            <Gauge percent={Number(domainRiskScoreResponse?.stats?.domain_risk_score?.overall_score || 0) / 900} />
                                            <p className="hacker-score my-2">{domainRiskScoreResponse?.stats?.domain_risk_score?.overall_score} <label>Max Score 900</label></p>
                                            <p className={`risk-level ${domainRiskScoreResponse?.stats?.domain_risk_score?.risk_level}`}>Risk level: {domainRiskScoreResponse?.stats?.domain_risk_score?.risk_level}</p>
                                        </div>
                                        {domainRiskScoreResponse?.stats?.domain_risk_score?.trend !== null && <div className="previous-scan-result">
                                            <p className="score-difference mb-3">{domainRiskScoreResponse?.stats?.domain_risk_score?.percentage_change}% {domainRiskScoreResponse?.stats?.domain_risk_score?.trend === "decreased" ? trendingDownIcon() : trendingUpIcon()}</p>
                                            <p className="scan-text">vs  Previous Scan</p>
                                        </div>}
                                    </div>
                                    <div className="scan-date-details">
                                        <p className="scan-date">Last scan date: <label>{formatDate(domainRiskScoreResponse?.stats?.domain_risk_score?.scan_date, "date")}</label></p>
                                        <p className="scan-date">Previous scan date: <label>{formatDate(domainRiskScoreResponse?.stats?.domain_risk_score?.previous_scan_date, "date")}</label></p>
                                    </div>
                            </motion.div>
                        }                        
                    </div>
                </Col>
                <Col lg="8">
                    <div className="bg-white-with-shadow p-4 h-100">
                        {
                            domainRiskScoreOverviewLoading ?
                            <SkeletonLoader type={"line"} count={5} currentPage={"domainRiskScoreOverview"} />
                            :
                            <motion.div
                                key={"domainRiskScoreOverview"}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                {
                                    isOverviewScanNotFound ?
                                    <>
                                        <p className="heading">Overview</p>
                                        <div className="overview-chart">
                                            {
                                                Array.from({ length : 5}).map((_, index)=>{
                                                    return (
                                                        <div className="d-flex align-items-center justify-content-between" key={`overview_${index}`}>
                                                            {Array.from({ length: 12 }).map((_, index) => {
                                                                return (
                                                                    <div className="scan" key={`month_${index}`}></div>
                                                                )
                                                            })}
                                                        </div>
                                                    )
                                                })
                                            }
                                            <div className="d-flex align-items-center justify-content-between">
                                                <p className="overview-chart-label">Jan</p>
                                                <p className="overview-chart-label">Feb</p>
                                                <p className="overview-chart-label">Mar</p>
                                                <p className="overview-chart-label">Apr</p>
                                                <p className="overview-chart-label">May</p>
                                                <p className="overview-chart-label">Jun</p>
                                                <p className="overview-chart-label">Jul</p>
                                                <p className="overview-chart-label">Aug</p>
                                                <p className="overview-chart-label">Sep</p>
                                                <p className="overview-chart-label">Oct</p>
                                                <p className="overview-chart-label">Nov</p>
                                                <p className="overview-chart-label">Dec</p>
                                            </div>
                                        </div>
                                    </>
                                    
                                    :
                                    <>
                                        <p className="heading">Overview</p>
                                        <div className="overview-chart">
                                            <div className="d-flex align-items-center justify-content-between">
                                                {
                                                    domainRiskScoreOverviewData?.stats?.domain_score?.week1?.length > 0 &&
                                                    domainRiskScoreOverviewData?.stats?.domain_score?.week1?.map((data, index)=>{
                                                        return (
                                                            <div className={`scan ${data?.risk_level !== null ? `risk-${data?.risk_level}` : "no-scan"}`} key={data.key || `week1_${index}`}
                                                                onMouseEnter={() => setHoveredItem(data.key)}
                                                                onMouseLeave={() => setHoveredItem(null)}
                                                                >
                                                                    {hoveredItem === data.key && data?.score !== null && <div className="tooltip-container position-absolute">
                                                                        <div className="tooltip-content">
                                                                            <div className="d-flex align-items-center gap-3">
                                                                                <div className="risk-score">
                                                                                    <p className="score">{data?.score}</p>
                                                                                    <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                                                </div>
                                                                                <div className="date-and-difference">
                                                                                    <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="tooltip-arrow"></div>
                                                                        </div>
                                                                    </div>}
                                                            </div>
                                                        )
                                                    })
                                                }   
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between">
                                                {
                                                    domainRiskScoreOverviewData?.stats?.domain_score?.week2?.length > 0 &&
                                                    domainRiskScoreOverviewData?.stats?.domain_score?.week2?.map((data, index)=>{
                                                        return (
                                                            <div className={`scan ${data?.risk_level !== null ? `risk-${data?.risk_level}` : "no-scan"}`} key={data.key || `week2_${index}`}
                                                                onMouseEnter={() => setHoveredItem(data.key)}
                                                                onMouseLeave={() => setHoveredItem(null)}
                                                                >
                                                                    {hoveredItem === data.key && data?.score !== null && <div className="tooltip-container position-absolute">
                                                                        <div className="tooltip-content">
                                                                            <div className="d-flex align-items-center gap-3">
                                                                                <div className="risk-score">
                                                                                    <p className="score">{data?.score}</p>
                                                                                    <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                                                </div>
                                                                                <div className="date-and-difference">
                                                                                    <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="tooltip-arrow"></div>
                                                                        </div>
                                                                    </div>}
                                                            </div>
                                                        )
                                                    })
                                                }                                
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between">
                                                {
                                                    domainRiskScoreOverviewData?.stats?.domain_score?.week3?.length > 0 &&
                                                    domainRiskScoreOverviewData?.stats?.domain_score?.week3?.map((data, index)=>{
                                                        return (
                                                            <div className={`scan ${data?.risk_level !== null ? `risk-${data?.risk_level}` : "no-scan"}`} key={data.key || `week3_${index}`}
                                                                onMouseEnter={() => setHoveredItem(data.key)}
                                                                onMouseLeave={() => setHoveredItem(null)}
                                                                >
                                                                    {hoveredItem === data.key && data?.score !== null && <div className="tooltip-container position-absolute">
                                                                        <div className="tooltip-content">
                                                                            <div className="d-flex align-items-center gap-3">
                                                                                <div className="risk-score">
                                                                                    <p className="score">{data?.score}</p>
                                                                                    <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                                                </div>
                                                                                <div className="date-and-difference">
                                                                                    <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="tooltip-arrow"></div>
                                                                        </div>
                                                                    </div>}
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between">
                                                {
                                                    domainRiskScoreOverviewData?.stats?.domain_score?.week4?.length > 0 &&
                                                    domainRiskScoreOverviewData?.stats?.domain_score?.week4?.map((data, index)=>{
                                                        return (
                                                            <div className={`scan ${data?.risk_level !== null ? `risk-${data?.risk_level}` : "no-scan"}`} key={data.key || `week4_${index}`}
                                                                onMouseEnter={() => setHoveredItem(data.key)}
                                                                onMouseLeave={() => setHoveredItem(null)}
                                                                >
                                                                    {hoveredItem === data.key && data?.score !== null && <div className="tooltip-container position-absolute">
                                                                        <div className="tooltip-content">
                                                                            <div className="d-flex align-items-center gap-3">
                                                                                <div className="risk-score">
                                                                                    <p className="score">{data?.score}</p>
                                                                                    <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                                                </div>
                                                                                <div className="date-and-difference">
                                                                                    <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="tooltip-arrow"></div>
                                                                        </div>
                                                                    </div>}
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between">
                                                {Array.from({ length: 12 }).map((_, monthIndex) => {
                                                    const data = domainRiskScoreOverviewData?.stats?.domain_score?.week5?.[monthIndex];
                                                    if (!data) return (<div className="scan no-scan" key={`week5_empty_${monthIndex}`}></div>)
                                                    return (
                                                        <div
                                                        key={`row_${monthIndex}`}
                                                        className={`scan ${data?.risk_level ? `risk-${data?.risk_level}` : "no-scan"}`}
                                                        onMouseEnter={() => setHoveredItem(data?.key)}
                                                        onMouseLeave={() => setHoveredItem(null)}
                                                        >
                                                        {hoveredItem === data.key && data?.score !== null && <div className="tooltip-container position-absolute">
                                                                        <div className="tooltip-content">
                                                                            <div className="d-flex align-items-center gap-3">
                                                                                <div className="risk-score">
                                                                                    <p className="score">{data?.score}</p>
                                                                                    <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                                                </div>
                                                                                <div className="date-and-difference">
                                                                                    <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="tooltip-arrow"></div>
                                                                        </div>
                                                                    </div>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <p className="overview-chart-label">Jan</p>
                                                <p className="overview-chart-label">Feb</p>
                                                <p className="overview-chart-label">Mar</p>
                                                <p className="overview-chart-label">Apr</p>
                                                <p className="overview-chart-label">May</p>
                                                <p className="overview-chart-label">Jun</p>
                                                <p className="overview-chart-label">Jul</p>
                                                <p className="overview-chart-label">Aug</p>
                                                <p className="overview-chart-label">Sep</p>
                                                <p className="overview-chart-label">Oct</p>
                                                <p className="overview-chart-label">Nov</p>
                                                <p className="overview-chart-label">Dec</p>
                                            </div>
                                        </div>
                                    </>
                                } 
                            </motion.div>
                        }
                    </div>
                </Col>
            </Row>
        </div>        
    )
}

export default DomainRiskScore;